import Button from "@/components/Button";
import { Checkbox } from "@/components/chadcn/Checkbox";
import { DialogContent, DialogFooter, DialogTitle } from "@/components/chadcn/Dialog";
import Dialog from "@/components/Dialog";
import Spinner from "@/components/Spinner";
import { useSetPatientEducationMutation } from "@/data/queries";
import { Organization } from "@/model/Organization";
import { useForm } from "react-hook-form";

type PatientEducationFormValues = {
  educationProvided: boolean;
};

type PatientEducationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organization: Organization;
  prescriptionId: string;
};

export const PatientEducationDialog = ({
  open,
  onOpenChange,
  organization,
  prescriptionId,
}: PatientEducationDialogProps) => {
  const {
    mutateAsync: setPatientEducation,
    isPending: isSettingPatientEducation,
    isError: isSetPatientEducationError,
  } = useSetPatientEducationMutation();

  const defaultValues: PatientEducationFormValues = {
    educationProvided: true,
  };

  const { handleSubmit, setValue, watch, reset } = useForm<PatientEducationFormValues>({
    defaultValues,
  });

  const { educationProvided } = watch();

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (open) {
      reset(defaultValues);
    }
  };

  const onSubmitInternal = async (form: PatientEducationFormValues) => {
    try {
      await setPatientEducation({
        prescriptionId,
        educationProvided: form.educationProvided,
      });
      handleOpenChange(false);
    } catch (error) {
      console.error(error);
    }
  };

  const canSubmit = educationProvided !== undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex min-w-[720px] max-w-[10%] flex-col gap-y-4 bg-white">
        <DialogTitle>Log Patient Education</DialogTitle>
        <form
          key={organization.id}
          className="flex flex-col gap-y-4"
          onSubmit={handleSubmit(onSubmitInternal)}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-primary/90">
              Patient education provided? <span className="text-red-500">*</span>
            </span>
            <YesNoCheckboxes
              checked={educationProvided}
              onCheckedChange={(checked) => setValue("educationProvided", checked)}
              disabled={true}
            />
          </div>
          {isSetPatientEducationError && (
            <p className="my-2 text-red-600">
              There was an error setting the patient education. Please contact Bloomlife Support, or
              try again later.
            </p>
          )}
          <DialogFooter>
            <div className="flex gap-2">
              <Button
                variant="neutral"
                type="button"
                onClick={() => handleOpenChange(false)}
                className="w-fit border-none bg-primary/10"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={!canSubmit || isSettingPatientEducation}
                className="w-fit"
              >
                Submit
                {isSettingPatientEducation && <Spinner className="ms-2 size-4 border-2" />}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

type YesNoCheckboxesProps = {
  checked?: boolean;
  onCheckedChange: (isChecked: boolean) => void;
  disabled?: boolean;
};

const YesNoCheckboxes = ({ checked, onCheckedChange, disabled }: YesNoCheckboxesProps) => (
  <div className="flex items-center gap-2">
    <Checkbox
      checked={checked === undefined ? false : checked}
      onCheckedChange={(checkedState) => onCheckedChange(checkedState === true)}
      disabled={disabled}
    />
    <span className="me-4 text-primary/90">Yes</span>
    <Checkbox
      checked={checked === undefined ? false : !checked}
      onCheckedChange={(checkedState) => onCheckedChange(checkedState === false)}
      disabled={disabled}
    />
    <span className="text-primary/90">No</span>
  </div>
);
